import { Countdown } from '@/components/Countdown';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  EventPassRedemptionFragment,
  FullEventFragment,
  PaymentSubscriptionFragment,
  useEndLiveSessionMutation,
} from '@/generated/graphql';
import { useAdminScreenThemeState } from '@/providers/AdminScreenThemeStateProvider/AdminScreenThemeStateContext';
import { formatDistanceToNow } from 'date-fns';
import { formatLocaleDate } from '@/utils/formatLocaleDate';

export function LiveDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { event } = useAdminScreenThemeState();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{event.name}</span>{' '}
            <span className="font-bold text-constructive">is live!</span>
          </DialogTitle>
          <DialogDescription>
            {/* TODO: Add a description of the live state */}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {/* TODO: show "card" based on the subscriptionInUse and eventPassRedemptions */}
          {event.subscriptionInUse && (
            <UsingSubscriptionCard
              event={event}
              subscription={event.subscriptionInUse}
              onEventUpdated={() => {
                onOpenChange(false);
              }}
            />
          )}
          {event.eventPassRedemptionInUse && (
            <UsingEventPassCard
              event={event}
              eventPassRedemption={event.eventPassRedemptionInUse}
            />
          )}
          {!event.subscriptionInUse && !event.eventPassRedemptionInUse && (
            <FreeLiveCard
              event={event}
              onEventUpdated={() => {
                onOpenChange(false);
              }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FreeLiveCard = ({
  event,
  onEventUpdated,
}: {
  event: FullEventFragment;
  onEventUpdated: (event: FullEventFragment) => void;
}) => {
  const [endLiveSession] = useEndLiveSessionMutation();

  const handleEndLiveSession = () => {
    endLiveSession({
      variables: {
        eventId: event.id,
      },
    }).then(({ data }) => {
      if (data?.endLiveSession) {
        onEventUpdated(data.endLiveSession);
      }
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <InstructionsCard event={event} />
      <Button
        variant="destructive"
        className="self-center"
        onClick={handleEndLiveSession}
      >
        End Live Session
      </Button>
    </div>
  );
};

const UsingSubscriptionCard = ({
  event,
  subscription,
  onEventUpdated,
}: {
  event: FullEventFragment;
  subscription: PaymentSubscriptionFragment;
  onEventUpdated: (event: FullEventFragment) => void;
}) => {
  const [endLiveSession] = useEndLiveSessionMutation();

  const handleEndLiveSession = () => {
    endLiveSession({
      variables: {
        eventId: event.id,
      },
    }).then(({ data }) => {
      if (data?.endLiveSession) {
        onEventUpdated(data.endLiveSession);
      }
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <Card
        data-name="USING_EVENT_PASS_CARD"
        className="border-epic bg-epic/80"
      >
        <CardHeader>
          <CardTitle className="-m-2">
            <span data-id={subscription.id}>Using Subscription</span>{' '}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-start justify-start gap-2 rounded bg-muted/30 p-2 text-xs">
            <div data-name="CURRENT_PERIOD" className="flex items-center gap-2">
              <span className="font-bold">Current Period:</span>
              {formatLocaleDate(subscription.currentPeriodStart)} -{' '}
              {formatLocaleDate(subscription.currentPeriodEnd)}
            </div>
            <div
              data-name="MAX_CONCURRENT_EVENTS"
              className="flex items-center gap-2"
            >
              <span className="font-bold">Active Screens:</span>{' '}
              <Badge variant="secondary">
                {subscription.liveEvents.length}
              </Badge>
              out of{' '}
              <Badge variant="secondary">
                {subscription.maxConcurrentEvents}
              </Badge>{' '}
              allowed
            </div>
          </div>
        </CardContent>
      </Card>
      <InstructionsCard event={event} />
      <Button
        variant="destructive"
        className="self-center"
        onClick={handleEndLiveSession}
      >
        End Live Session
      </Button>
    </div>
  );
};

const UsingEventPassCard = ({
  event,
  eventPassRedemption,
}: {
  event: FullEventFragment;
  eventPassRedemption: EventPassRedemptionFragment;
}) => {
  return (
    <>
      <Card
        data-name="USING_EVENT_PASS_CARD"
        className="border-constructive bg-constructive/80"
      >
        <CardHeader>
          <CardTitle>
            <span data-id={eventPassRedemption.id}>Using Event Pass</span>{' '}
          </CardTitle>
          <CardDescription>
            Screen went live{' '}
            <span
              title={new Date(eventPassRedemption.redeemedAt).toLocaleString()}
            >
              {formatDistanceToNow(eventPassRedemption.redeemedAt, {
                addSuffix: true,
              })}
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div>
            <div className="flex items-center gap-2 text-xl">
              Time remaining:
              <Countdown
                className="text-3xl font-bold"
                until={new Date(eventPassRedemption.expiresAt)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <InstructionsCard event={event} />
    </>
  );
};

/**
 * Returns the URL to the code entry page for the event.
 * If the origin is localhost, it should return HOST:PORT/stage.
 * If the origin is happiscreen.com, it should return https://my.happiscreen.com
 */
function useCodeEntryUrl() {
  const origin = window.location.origin;
  if (origin.startsWith('http://localhost')) {
    return `${origin}/stage`;
  }
  // return `https://my.happiscreen.com`;
  return `https://happiscreen.com/stage`;
}

function InstructionsCard({ event }: { event: FullEventFragment }) {
  const codeEntryUrl = useCodeEntryUrl();

  return (
    <Card className="flex flex-col items-start gap-2">
      <CardHeader>
        <CardTitle>
          <span className="text-2xl font-bold">What to do now</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <h3 className="text-lg font-bold">1. Open stage on source device</h3>
        <div className="flex flex-col gap-2 p-4">
          <p>
            On the device you plan to cast your screen from, go to{' '}
            <a href={codeEntryUrl} target="_blank" rel="noopener noreferrer">
              {codeEntryUrl}
            </a>{' '}
            and enter the code:
          </p>
          {event.screenCode && (
            <div className="flex items-center justify-center gap-2 self-center rounded-lg bg-muted p-3">
              <span className="font-mono text-4xl font-bold tracking-wider">
                {event.screenCode}
              </span>
            </div>
          )}
          <p>You can even cast to multiple screens!</p>
        </div>

        <h3 className="text-lg font-bold">2. Cast to your stage screen</h3>
        <div className="flex flex-col gap-2 p-4">
          <p>
            Cast your screen to the stage screen (TV, projector, virtual
            screenshare, etc.). Users should be able to see the stage screen and
            interact with it!
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
